import { useCallback, useEffect, useMemo, useState } from "react";

import { api, type AppSettingItem } from "@/lib/api";

/**
 * Página de Configurações (Fase 5 C3).
 *
 * O objetivo é responder, sem abrir o .env: **o que está ativo agora, de onde
 * veio esse valor e o que essa opção faz**. Por isso cada item mostra rótulo em
 * português, explicação, valor efetivo, origem e padrão — o nome técnico da
 * chave fica em segundo plano.
 *
 * As abas e as descrições vêm do backend (`settings_catalog.py`), que é a
 * fonte única: adicionar ou ocultar um parâmetro é mudança de um lugar só.
 */

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

const ORIGIN: Record<AppSettingItem["origin"], { text: string; className: string }> = {
  db: {
    text: "definido aqui",
    className: "border-emerald-700 bg-emerald-900/40 text-emerald-300",
  },
  env: {
    text: "vindo do .env",
    className: "border-amber-700 bg-amber-900/30 text-amber-300",
  },
  default: {
    text: "padrão do sistema",
    className: "border-ink-600 bg-ink-700/60 text-ink-300",
  },
};

function SettingCard({
  item,
  onSave,
  onReset,
  busy,
}: {
  item: AppSettingItem;
  onSave: (key: string, value: unknown) => Promise<void>;
  onReset: (key: string) => Promise<void>;
  busy: boolean;
}) {
  const [draft, setDraft] = useState(() => formatValue(item.value));
  const [error, setError] = useState<string | null>(null);
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    setDraft(formatValue(item.value));
    setError(null);
  }, [item.value]);

  const dirty = draft !== formatValue(item.value);
  const origin = ORIGIN[item.origin];

  const save = useCallback(
    async (raw: unknown) => {
      setError(null);
      try {
        await onSave(item.key, raw);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [item.key, onSave],
  );

  function renderControl() {
    if (item.type === "bool") {
      const on = draft === "true";
      return (
        <button
          type="button"
          role="switch"
          aria-checked={on}
          disabled={busy}
          onClick={() => {
            const next = !on;
            setDraft(next ? "true" : "false");
            void save(next);
          }}
          className={`flex items-center gap-2 rounded border px-3 py-1.5 text-xs transition ${
            on
              ? "border-emerald-600 bg-emerald-900/40 text-emerald-200"
              : "border-ink-600 bg-ink-800 text-ink-300"
          } disabled:opacity-50`}
        >
          <span
            className={`h-2 w-2 rounded-full ${on ? "bg-emerald-400" : "bg-ink-500"}`}
            aria-hidden="true"
          />
          {on ? "Ativado" : "Desativado"}
        </button>
      );
    }

    if (item.choices && item.choices.length > 0) {
      return (
        <select
          value={draft}
          disabled={busy}
          onChange={(event) => {
            setDraft(event.target.value);
            void save(event.target.value);
          }}
          className="rounded border border-ink-600 bg-ink-900 px-2 py-1.5 text-xs text-ink-100"
        >
          {item.choices.map((choice) => (
            <option key={choice} value={choice}>
              {choice}
            </option>
          ))}
        </select>
      );
    }

    return (
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          value={draft}
          disabled={busy}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && dirty) void save(draft);
            if (event.key === "Escape") setDraft(formatValue(item.value));
          }}
          className="w-40 rounded border border-ink-600 bg-ink-900 px-2 py-1.5 text-xs text-ink-100"
        />
        {item.unit && <span className="text-[11px] text-ink-400">{item.unit}</span>}
        {dirty && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void save(draft)}
            className="rounded border border-emerald-700 bg-emerald-900/40 px-2 py-1.5 text-[11px] text-emerald-200 hover:bg-emerald-900/70"
          >
            Salvar
          </button>
        )}
      </div>
    );
  }

  return (
    <article className="border-b border-ink-800 py-4 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 max-w-2xl flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-medium text-ink-100">{item.label}</h3>
            <span className={`rounded border px-1.5 py-0.5 text-[10px] ${origin.className}`}>
              {origin.text}
            </span>
            {item.requires_restart && (
              <span
                className="rounded border border-orange-700 bg-orange-900/30 px-1.5 py-0.5 text-[10px] text-orange-300"
                title="Só passa a valer quando os serviços forem reiniciados."
              >
                requer reinício
              </span>
            )}
          </div>

          <p className="mt-1.5 text-xs leading-relaxed text-ink-400">{item.description}</p>

          <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-ink-500">
            <span>
              Padrão: <code className="text-ink-400">{formatValue(item.default) || "(vazio)"}</code>
            </span>
            <button
              type="button"
              onClick={() => setShowKey((value) => !value)}
              className="underline decoration-dotted underline-offset-2 hover:text-ink-300"
            >
              {showKey ? "ocultar nome técnico" : "nome técnico"}
            </button>
            {showKey && <code className="font-mono text-ink-400">{item.key}</code>}
          </div>

          {error && <p className="mt-2 text-[11px] text-red-400">{error}</p>}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {renderControl()}
          {item.origin === "db" && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void onReset(item.key)}
              className="rounded border border-ink-600 bg-ink-800 px-2 py-1.5 text-[11px] text-ink-300 hover:border-ink-500 hover:text-ink-100"
              title="Remove o valor definido aqui e volta ao .env ou ao padrão do sistema"
            >
              Restaurar
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export function Settings() {
  const [items, setItems] = useState<AppSettingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await api.listAppSettings());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const replaceItem = useCallback((updated: AppSettingItem) => {
    setItems((current) => current.map((item) => (item.key === updated.key ? updated : item)));
  }, []);

  const handleSave = useCallback(
    async (key: string, value: unknown) => {
      setBusyKey(key);
      try {
        replaceItem(await api.updateAppSetting(key, value));
      } finally {
        setBusyKey(null);
      }
    },
    [replaceItem],
  );

  const handleReset = useCallback(
    async (key: string) => {
      setBusyKey(key);
      try {
        replaceItem(await api.resetAppSetting(key));
      } finally {
        setBusyKey(null);
      }
    },
    [replaceItem],
  );

  // A ordem das abas é a que o backend devolve (definida no catálogo).
  const tabs = useMemo(() => {
    const seen: string[] = [];
    for (const item of items) if (!seen.includes(item.tab)) seen.push(item.tab);
    return seen;
  }, [items]);

  useEffect(() => {
    if (activeTab === null && tabs.length > 0) setActiveTab(tabs[0]);
  }, [activeTab, tabs]);

  const term = search.trim().toLowerCase();
  const searching = term.length > 0;

  const matches = useCallback(
    (item: AppSettingItem) =>
      !searching ||
      item.label.toLowerCase().includes(term) ||
      item.description.toLowerCase().includes(term) ||
      item.key.toLowerCase().includes(term),
    [searching, term],
  );

  // Buscando, mostramos todas as abas juntas — o usuário não sabe em qual está.
  const visible = useMemo(
    () => items.filter((item) => matches(item) && (searching || item.tab === activeTab)),
    [activeTab, items, matches, searching],
  );

  const changedCount = items.filter((item) => item.origin === "db").length;
  const countPerTab = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      if (item.origin !== "db") continue;
      counts.set(item.tab, (counts.get(item.tab) ?? 0) + 1);
    }
    return counts;
  }, [items]);

  const grouped = useMemo(() => {
    if (!searching) return [[activeTab ?? "", visible] as const];
    const map = new Map<string, AppSettingItem[]>();
    for (const item of visible) map.set(item.tab, [...(map.get(item.tab) ?? []), item]);
    return [...map.entries()];
  }, [activeTab, searching, visible]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="border-b border-ink-700 px-6 pt-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-ink-50">Configurações</h1>
            <p className="mt-1 max-w-3xl text-xs leading-relaxed text-ink-400">
              O que está ativo agora e de onde veio cada valor. A ordem de prioridade é{" "}
              <strong className="text-ink-200">o que você define aqui</strong> &gt; arquivo{" "}
              <code>.env</code> &gt; padrão do sistema. <strong className="text-ink-200">Restaurar</strong>{" "}
              apaga o valor definido aqui e devolve o parâmetro à origem anterior. Senhas, chaves de
              acesso e caminhos de servidor não aparecem nesta tela — continuam no <code>.env</code>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar em todas as abas..."
              className="w-56 rounded border border-ink-600 bg-ink-900 px-2 py-1.5 text-xs text-ink-100"
            />
            <button
              type="button"
              onClick={() => void load()}
              className="rounded border border-ink-600 bg-ink-800 px-2.5 py-1.5 text-xs text-ink-100 hover:border-emerald-600"
            >
              Recarregar
            </button>
          </div>
        </div>

        {changedCount > 0 && (
          <p className="mt-2 text-[11px] text-emerald-400">
            {changedCount} {changedCount === 1 ? "parâmetro definido" : "parâmetros definidos"} nesta tela.
          </p>
        )}

        <nav className="-mb-px mt-3 flex flex-wrap gap-1" aria-label="Áreas de configuração">
          {tabs.map((tab) => {
            const active = !searching && tab === activeTab;
            const changed = countPerTab.get(tab) ?? 0;
            return (
              <button
                key={tab}
                type="button"
                aria-current={active ? "page" : undefined}
                onClick={() => {
                  setSearch("");
                  setActiveTab(tab);
                }}
                className={`flex items-center gap-1.5 rounded-t border-b-2 px-3 py-2 text-xs transition ${
                  active
                    ? "border-emerald-500 bg-ink-800/60 text-ink-50"
                    : "border-transparent text-ink-400 hover:text-ink-200"
                }`}
              >
                {tab}
                {changed > 0 && (
                  <span className="rounded-full bg-emerald-900/60 px-1.5 text-[10px] text-emerald-300">
                    {changed}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </header>

      <div className="min-h-0 flex-1 overflow-auto px-6 py-2">
        {loading && <p className="py-4 text-sm text-ink-400">Carregando...</p>}
        {error && <p className="py-4 text-sm text-red-400">{error}</p>}
        {!loading && !error && visible.length === 0 && (
          <p className="py-4 text-sm text-ink-500">
            {searching ? "Nada encontrado para essa busca." : "Nenhum parâmetro nesta aba."}
          </p>
        )}

        {grouped.map(([tab, tabItems]) =>
          tabItems.length === 0 ? null : (
            <section key={tab || "todos"}>
              {searching && (
                <h2 className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-ink-500">
                  {tab}
                </h2>
              )}
              {tabItems.map((item) => (
                <SettingCard
                  key={item.key}
                  item={item}
                  onSave={handleSave}
                  onReset={handleReset}
                  busy={busyKey === item.key}
                />
              ))}
            </section>
          ),
        )}
      </div>
    </div>
  );
}
